import { FC } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex, Grid, HStack, Image, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';

import { PackageType } from '__generated__/GQL';
import shineLogo from 'assets/shine-logo.svg';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import ReferralOffer from '../../components/ReferralOffer';
import { OnboardingPackagesDocument } from '../../graphql/queries/onboardingPackages.gql';
import amountWithMinorCurrency from '../../helpers/amountWithMinorCurrency';
import { OnboardingPackageItem } from '../../machine/model';
import PlanPrice from '../PlanSelection/components/PlanPrice';
import PackageCouponInput from './components/PackageCouponInput';
import formatPrice from './lib/formatPrice';
import getPackageRecapFields from './lib/getPackageRecapFields';
import locales from './locales';

interface PackageRecapProps {
  onNext: () => void;
  pack: OnboardingPackageItem;
  onCouponApplied: (couponId: string) => void;
  coupon: string | null;
  companyProfileId: string;
  referrer: string | null;
  shouldShowPackagePriceWithVAT: boolean;
}

/**
 * A page where the user can see the outstanding agreement and accept it.
 */
const PackageRecap: FC<PackageRecapProps> = ({
  companyProfileId,
  coupon,
  onCouponApplied,
  onNext,
  pack,
  referrer,
  shouldShowPackagePriceWithVAT,
}) => {
  const fields = getPackageRecapFields({
    pack,
  });

  const discountPrice = shouldShowPackagePriceWithVAT
    ? pack.discountPrice
    : pack.discountPriceWithoutVAT;
  const basePrice = shouldShowPackagePriceWithVAT
    ? pack.basePrice
    : pack.basePriceWithoutVAT;
  const vatForPackagePrice = shouldShowPackagePriceWithVAT
    ? locales.vat.included
    : locales.vat.excluded;

  return (
    <Box>
      <Header title={fields.pageTitle} />

      <Box>
        <Box padding="space-24">
          <Grid
            gap="space-24"
            gridTemplateAreas="'first divider second'"
            gridTemplateColumns="1fr auto 1fr"
          >
            <Heading
              alignSelf="center"
              gridArea="divider"
              size="2xl"
              variant="number"
            >
              +
            </Heading>

            {fields.claims.map((claim) => (
              <VStack key={claim.label} textAlign="center">
                <Image boxSize="80px" src={claim.icon} />
                <Text fontWeight="weight-500" size="sm" variant="primary">
                  {claim.label}
                </Text>
              </VStack>
            ))}
          </Grid>
        </Box>

        {referrer ? (
          <ReferralOffer
            isAlreadyApplied
            margin="space-24"
            referrer={referrer}
          />
        ) : null}

        {pack.promotionalOffer ? (
          <Grid
            alignItems="center"
            gap="space-24"
            gridTemplateColumns="1fr auto 1fr"
            marginY="space-20"
          >
            <Image justifySelf="flex-end" src={shineLogo} width="60px" />

            <Text
              alignSelf="center"
              fontSize="font-16"
              fontWeight="weight-500"
              variant="light"
            >
              x
            </Text>

            {pack.promotionalOffer.affiliateLogoSource ? (
              <Image
                maxHeight="40px"
                maxWidth="40px"
                src={pack.promotionalOffer.affiliateLogoSource}
              />
            ) : (
              <Text fontWeight="weight-500">
                {pack.promotionalOffer.affiliateName}
              </Text>
            )}
          </Grid>
        ) : null}

        <Flex align="center" direction="column" padding="space-24">
          <HStack>
            <PlanPrice
              value={amountWithMinorCurrency(discountPrice)}
              withoutCents={amountWithMinorCurrency(discountPrice) % 1 === 0}
            />

            <Heading size="xl" variant="number">
              {vatForPackagePrice}
            </Heading>
          </HStack>

          <HStack>
            {discountPrice < basePrice ? (
              <Text size="sm" variant="light">
                {locales.formatString(locales.claims.discounted, {
                  basePrice: formatPrice(basePrice),
                  taxMention: vatForPackagePrice,
                })}
              </Text>
            ) : null}
          </HStack>

          <Text>{fields.bankAccount.label}</Text>
          <Text fontWeight="weight-500" variant="light">
            {fields.bankAccount.secondary}
          </Text>
        </Flex>
      </Box>

      <PackageCouponInput
        appliedCoupon={coupon}
        companyProfileId={companyProfileId}
        onCouponApplied={onCouponApplied}
        pack={pack}
      />

      <Box paddingTop="space-24" textAlign="center">
        <Button isExpanded onClick={onNext}>
          {locales.continue}
        </Button>
      </Box>
    </Box>
  );
};

interface PackageRecapContainerProps {
  onNext: () => void;
  packageType: PackageType;
  providerPackageId: string;
  onCouponApplied: (couponId: string) => void;
  coupon: string | null;
  companyProfileId: string;
  onPrev: () => void;
  referrer: string | null;
  shouldShowPackagePriceWithVAT: boolean;
}

const PackageRecapContainer = asBentoPage<PackageRecapContainerProps>(
  ({
    companyProfileId,
    coupon,
    Layout,
    onPrev,
    packageType,
    providerPackageId,
    ...props
  }) => {
    const query = useQuery(OnboardingPackagesDocument, {
      variables: {
        companyProfileId,
        input: {
          coupon: coupon ?? undefined,
          type: packageType,
        },
      },
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <AnimatePresence>
          <motion.div exit={{ opacity: 0, y: 10 }} key={`coupon:${coupon}`}>
            <SuspenseQuery query={query}>
              {({ viewer: { company } }) => {
                const pack = company.onboardingPackages.find(
                  (p) => p.providerPackageId === providerPackageId,
                );

                if (!pack) {
                  throw new Error('Package not found');
                }

                return (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <PackageRecap
                      {...props}
                      companyProfileId={company.companyProfileId}
                      coupon={coupon}
                      pack={pack as OnboardingPackageItem} // Casting `providerPackageId` to union type
                    />
                  </motion.div>
                );
              }}
            </SuspenseQuery>
          </motion.div>
        </AnimatePresence>
      </Layout>
    );
  },
);

export default PackageRecapContainer;
